import React, { useEffect, useState } from "react";
import { Button } from "../ui/Button";
import { ImageLightbox } from "../ui/ImageLightbox";
import type { ComboDeal } from "../../types/content.types";

interface ComboDealsSectionProps {
  heading: string;
  combos: ComboDeal[];
  ctaText: string;
  onCtaClick: (comboTitle: string) => void;
}

export function ComboDealsSection({
  heading,
  combos,
  ctaText,
  onCtaClick,
}: ComboDealsSectionProps) {
  const [lightboxImage, setLightboxImage] = useState<{
    url: string;
    alt: string;
  } | null>(null);

  // Preload all combo deal images on component mount
  useEffect(() => {
    combos.forEach((combo) => {
      const img = new Image();
      img.src = combo.image;
    });
  }, [combos]);
  return (
    <section className="bg-black py-20 px-4">
      <div className="w-full max-w-6xl mx-auto px-4">
        <div className="flex flex-col items-center text-center gap-8">
          {/* Heading */}
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#a79c68]">
            {heading}
          </h2>

          {/* Two Combo Deals Side-by-Side */}
          <div className="grid grid-cols-2 gap-4 md:gap-8 w-full">
            {combos.map((combo, index) => (
              <div key={index} className="flex flex-col gap-4">
                {/* Combo Title */}
                <h3 className="text-sm sm:text-base md:text-lg font-bold text-[#a79c68] text-center min-h-[5rem] sm:min-h-[4rem] flex items-start justify-center whitespace-pre-line">
                  {combo.title}
                </h3>

                {/* Combo Image */}
                <div
                  className="relative aspect-[4/3] rounded-xl overflow-hidden bg-black cursor-pointer transition-transform hover:scale-105"
                  onClick={() =>
                    setLightboxImage({ url: combo.image, alt: combo.title })
                  }
                >
                  <img
                    src={combo.image}
                    alt={combo.title}
                    className="w-full h-full object-cover"
                    loading="eager"
                    fetchPriority="high"
                  />
                </div>

                {/* Price */}
                <p className="text-xl font-semibold text-gray-300 text-center">
                  {combo.price}
                </p>
              </div>
            ))}
          </div>

          {/* Single CTA Button Below Both Combos */}
          <div className="flex justify-center mt-4">
            <Button
              variant="primary"
              size="lg"
              onClick={() => onCtaClick("Combo Deals")}
            >
              {ctaText}
            </Button>
          </div>
        </div>
      </div>

      {/* Image Lightbox */}
      <ImageLightbox
        imageUrl={lightboxImage?.url || ""}
        imageAlt={lightboxImage?.alt || ""}
        isOpen={!!lightboxImage}
        onClose={() => setLightboxImage(null)}
        ctaText={ctaText}
        onCtaClick={() => {
          if (lightboxImage) {
            onCtaClick(lightboxImage.alt);
          }
        }}
      />
    </section>
  );
}
